import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTotalWeight, useTotalCubes } from './hooks';
import { LocationOn, NotListedLocation, Warning as WarningIcon } from '@material-ui/icons';
import CheckIcon from '@material-ui/icons/Check';
import CreateIcon from '@material-ui/icons/Create';
import { IconButton, Tooltip } from '@material-ui/core';
import { aggregateEvents, calcMilestone, genAttributes, getStorageDays } from '@onward-delivery/core';
import { delWindowToFormattedDateRange } from '@/utilities/delWindowToFormattedDate';
import { calcOrderItemTotal } from '@/utilities/calculateItemMetrics';
import { calcOrderRateOwed } from '@/utilities/calcOrderPricing';

import { BodyCellText } from './blocks';
import { destinationAddress, destinationAddress2 } from './utils';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { startCase } from 'lodash';
import { forwardStockedStatusLabel } from '@/utilities/forwardStockedStatus';
import { RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { colors } from '@/styles';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { toNational } from '@/utilities/formatPhoneNumber';
import { toFullState } from '@/utilities/address';
import { getMilestoneStyles } from '@/utilities/milestoneStyles';
import { findMatchingWarehouseEvent } from '@/utilities/warehouseEvents';
import { dispatch_integration_types } from './filterOptions';

const Placeholder = styled.div`
    width: 24px;
    height: 24px;
`;

export const integerFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const dateShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const dateTimeShort = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
});

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const rate = (rate, override, listingRate) => {
    if (override || override === 0) return `$${override.toFixed(2)}`;
    if (listingRate) {
        return `$${listingRate.toFixed(2)}`;
    }
    return rate ? `$${rate.toFixed(2)}` : '-';
};

const INTEGRATION_TYPES_BY_KEY = Object.fromEntries(dispatch_integration_types.map((t) => [t.value, t.label]));

export const useColumns = ({ tab, onwardAdminsById, callbacks = {} }) => {
    const dateSort = useMemo(() => (rowA, rowB, columnId) => {
        const a = rowA.values.deldate;
        const b = rowB.values.deldate;

        return a > b ? 1 : a < b ? -1 : 0;
    });

    const { circles, userType, email, user_id, isImposter, accountType, roles, partnerCarriersById } = useClientUser();

    // Hoisted out of the accessor, which react-table calls once per row per render.
    const viewerIds = useMemo(() => [user_id], [user_id]);

    const carrierRate = [
        {
            Header: 'Rate',
            id: 'rate',
            disableSortBy: false,
            width: 150,
            accessor: (order) => {
                const rate = calcOrderRateOwed(order, viewerIds);
                return rate ? dollarFormatter.format(rate) : '--';
            },
        },
    ];

    return useMemo(() => {
        return [
            {
                Header: '',
                id: 'order-details-group',
                columns: [
                    {
                        id: 'order_number',
                        Header: 'Order',
                        accessor: (order) => order.order_number,
                        // disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'po_number',
                        Header: 'PO',
                        accessor: (order) => order.po_number,
                        Cell: ({ value: numbers }) => {
                            if (numbers) {
                                let split = numbers.trim().split(', ');
                                return `${split.slice(0, 2).join(', ')}${
                                    split.length > 2 ? `... and ${split.length - 2} more` : ''
                                }`;
                            }

                            return '-';
                        },
                        // disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'reference_id',
                        Header: 'Reference ID',
                        accessor: (order) => order?.reference_id || '-',
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'service_type',
                        Header: 'Service Type',
                        accessor: (order) => {
                            const { location_type } = genAttributes(order);
                            return order.service_level?.service_level || LOCATION_TYPES[order[location_type]] || '-';
                        },
                        width: 200,
                    },
                    {
                        id: 'customer',
                        Header: 'Customer',
                        accessor: (order) => order.dropoff_name,
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 1;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        id: 'assigned_admin',
                        Header: 'Assigned Admin',
                        accessor: (order) => {
                            const adminId = order?.assigned_admin_id;
                            const rawLabel = adminId ? onwardAdminsById?.[adminId] || adminId : '-';

                            const marketplaceTier =
                                order?.price_breakdown?.shipper_breakdown?.marketplace_tier ||
                                order?.price_breakdown?.shipperBreakdown?.marketplace_tier ||
                                null;

                            const shouldMask =
                                !roles?.['ONWARD_ADMIN'] &&
                                typeof marketplaceTier === 'string' &&
                                /^T[23]/.test(marketplaceTier);

                            if (shouldMask) return 'Onward';
                            return rawLabel;
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    ...(accountType === 'carrier' ? [...carrierRate] : []),
                    {
                        // Hidden by default (DEFAULT_TABLE_INITIAL_STATE) -- the street address is
                        // wide and most users want city/state/zip. The export picker offers it
                        // regardless, via EXPORT_ONLY_COLUMNS, while this column stays hidden.
                        id: 'destination_address',
                        Header: 'Destination Address',
                        accessor: (order) => destinationAddress(order),
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 1;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        // Unit / apt / suite -- Address Line 2. Hidden by default alongside
                        // Line 1; most orders have nothing in it.
                        id: 'destination_address_2',
                        Header: 'Destination Address 2',
                        accessor: (order) => destinationAddress2(order),
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 1;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        id: 'destination_city',
                        Header: 'Destination City',
                        accessor: (order) => {
                            const { city } = genAttributes(order);
                            return order[city] || '-';
                        },
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 1;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        id: 'destination_state',
                        Header: 'Destination State',
                        accessor: (order) => {
                            const { state } = genAttributes(order);
                            return toFullState(order[state]) || '-';
                        },
                        width: 200,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 1;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'destination_zip',
                        Header: 'Destination Zip',
                        accessor: (order) => {
                            const { zip } = genAttributes(order);
                            return order[zip] || '-';
                        },
                        width: 150,
                        disableSortBy: true,
                    },
                    {
                        id: 'order_type',
                        Header: 'Order Type',
                        accessor: (order) => startCase(order.order_type),
                        width: 175,
                        disableSortBy: true,
                    },
                    {
                        id: 'warehousestatus',
                        Header: 'Warehousing Status',
                        accessor: (order) => {
                            const warehouse = aggregateEvents(order.wh_events);
                            return warehouse.status;
                        },
                        disableSortBy: true,
                        Cell: ({ row, value }) => {
                            let fragment;
                            let label;
                            let icon = null;
                            const order = row.original;
                            switch (value) {
                                case 'RECEIVED': {
                                    label = 'Received';
                                    fragment = css`
                                        color: ${colors.greens.primary};
                                    `;
                                    break;
                                }
                                case 'NOT_DELIVERED':
                                default: {
                                    const [received, total] = (order.itemsByOrderId || []).reduce(
                                        ([recAcc, totAcc], item) => {
                                            return [
                                                recAcc +
                                                    (RECEIVED_STATUSES.includes(item.pallet?.warehouse_status)
                                                        ? item.quantity || 1
                                                        : 0),
                                                totAcc + (item.quantity || 1),
                                            ];
                                        },
                                        [0, 0]
                                    );

                                    if (received > 0) {
                                        label = `${received}/${total} Received`;
                                        fragment = css`
                                            color: ${colors.blues[0]};
                                        `;
                                    } else {
                                        label = 'Not Received';
                                        fragment = css`
                                            color: ${colors.greys[3]};
                                        `;
                                    }
                                    break;
                                }
                            }

                            return (
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        white-space: nowrap;
                                    `}
                                >
                                    <BodyCellText
                                        css={css`
                                            font-weight: 700;
                                            ${fragment};
                                        `}
                                    >
                                        {label}
                                    </BodyCellText>
                                    {icon}
                                </div>
                            );
                        },
                        width: 200,
                    },
                    {
                        id: 'received_date',
                        Header: 'Actual Received Date',
                        accessor: (order) => {
                            const warehouse = aggregateEvents(order.wh_events);
                            return new Date(warehouse.received_date);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'dwell_days',
                        Header: 'Dwell',
                        accessor: (order) => getStorageDays(order),
                        Cell: ({ value }) => (Number.isFinite(value) ? `${value} days` : '-'),
                        disableSortBy: true,
                        width: 120,
                    },
                    {
                        id: 'deldate',
                        Header: 'Scheduled Date',
                        accessor: (order) => {
                            return order?.delivery_date_formatted || '-';
                        },
                        width: 200,
                    },
                    {
                        id: 'manufacturer',
                        Header: 'Manufacturer',
                        accessor: (order) => order?.manufacturer || '-',
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'pieces',
                        Header: 'Pieces',
                        accessor: (order) => calcOrderItemTotal([order], 'quantity'),
                        Cell: ({ value }) => integerFormatter.format(value || 0),
                        width: 110,
                        disableSortBy: true,
                    },
                    {
                        id: 'vol',
                        Header: 'Cu. Ft.',
                        Cell: ({ row: order }) => {
                            const cubes = useTotalCubes([order.original]);

                            return integerFormatter.format(cubes);
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'weight',
                        Header: 'Weight (lbs)',
                        Cell: ({ row: order }) => {
                            const weight = useTotalWeight([order.original]);

                            return integerFormatter.format(weight);
                        },
                        disableSortBy: true,
                    },
                    {
                        Header: 'Estimated Delivery Date Range',
                        id: 'estimated_delivery_range',
                        width: 200,
                        disableSortBy: false,
                        Cell: ({ row: order }) => {
                            if (
                                !order.original.estimated_delivery_range_end &&
                                !order.original.estimated_delivery_range_end
                            ) {
                                return '-';
                            }
                            return `${order.original.estimated_delivery_range_start_formatted} - ${order.original.estimated_delivery_range_end_formatted}`;
                        },
                    },
                    {
                        id: 'target_delivery_date',
                        Header: 'Target Delivery Date',
                        accessor: (order) => {
                            return new Date(order?.target_delivery_date);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'shipper',
                        Header: 'Shipper',
                        accessor: (order) => order.order_shipper?.business_name || '-',
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'carrier',
                        Header: 'Agent',
                        accessor: (order) => carrierDisplayName(order.order_carrier, partnerCarriersById) || '-',
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'order_program',
                        Header: 'Order Program',
                        accessor: (order) => order.program?.display_name || '-',
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'dropoff_phone',
                        Header: 'Customer Primary Phone',
                        accessor: (order) => {
                            return toNational(order.dropoff_phone, 'N/A');
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'secondary_dropoff_contact_phone',
                        Header: 'Customer Secondary Phone',
                        accessor: (order) => {
                            return toNational(order.secondary_dropoff_contact_phone, 'N/A');
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'dropoff_email',
                        Header: 'Customer Email',
                        accessor: (order) => order.dropoff_email || '-',
                        width: 265,
                        disableSortBy: true,
                    },
                    {
                        id: 'dispatch_integration_type',
                        Header: 'Agent TMS',
                        accessor: (order) => {
                            return INTEGRATION_TYPES_BY_KEY[order.dispatch_integration_type] || '-';
                        },
                        Cell: ({ value }) => {
                            return value || '-';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'dispatch_integration_meta.tracking_number',
                        Header: 'Agent TMS #',
                        width: 175,
                        accessor: (order) => {
                            return order.dispatch_integration_meta?.tracking_number;
                        },
                        Cell: ({ row: order, value }) => {
                            return value || '-';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'middle_mile_integration_meta.tracking_number',
                        Header: 'Middle Mile Tracking #',
                        width: 175,
                        accessor: (order) => {
                            return order.middle_mile_integration_meta?.tracking_number;
                        },
                        Cell: ({ row: order, value }) => {
                            return value || '-';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'middle_mile_integration_status',
                        Header: 'Middle Mile Status',
                        width: 200,
                        accessor: (order) => {
                            return order.middle_mile_integration_status;
                        },
                        Cell: ({ row: order, value }) => {
                            return value ? startCase(value.toLowerCase()).replace('_', '-') : '--';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'forward_stocked_status',
                        Header: 'Forward Stocked Status',
                        width: 200,
                        accessor: (order) => forwardStockedStatusLabel(order.forward_stocked_status),
                        disableSortBy: true,
                    },
                    {
                        id: 'est_ship_date',
                        Header: 'Est Ship Date',
                        accessor: (order) => {
                            const warehouse = aggregateEvents(order.wh_events);
                            return new Date(warehouse.est_ship_date);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'est_received_date',
                        Header: 'Est Received Date',
                        accessor: (order) => {
                            const warehouse = aggregateEvents(order.wh_events);
                            return new Date(warehouse.est_received_date);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'origin_city',
                        Header: 'Origin City',
                        accessor: (order) => {
                            const { city } = genAttributes(order, 'PICKUP');
                            return order[city] || '-';
                        },
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 2;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        id: 'origin_state',
                        Header: 'Origin State',
                        accessor: (order) => {
                            const { state } = genAttributes(order, 'PICKUP');
                            return toFullState(order[state]) || '-';
                        },
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            return (
                                <BodyCellText
                                    css={css`
                                        display: -webkit-box;
                                        -webkit-line-clamp: 2;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        font-weight: 500;
                                    `}
                                >
                                    {value}
                                </BodyCellText>
                            );
                        },
                    },
                    {
                        id: 'origin_zip',
                        Header: 'Origin Zip',
                        accessor: (order) => {
                            const { zip } = genAttributes(order, 'PICKUP');
                            return order[zip] || '-';
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'last_modified_at',
                        Header: 'Last Updated',
                        accessor: (order) => {
                            return new Date(order?.last_modified_at);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'created_at',
                        Header: 'Created At',
                        accessor: (order) => {
                            return new Date(order?.created_at);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'delivery_date_scheduled_at',
                        Header: 'Scheduled On',
                        accessor: (order) => {
                            return new Date(order?.delivery_date_scheduled_at);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'loaded_at',
                        Header: 'Loaded At',
                        accessor: (order) =>
                            new Date(
                                order?.loading_completed ??
                                    findMatchingWarehouseEvent(order?.wh_events, { action: 'PICKED_UP:CONFIRM' })
                                        ?.pickup_date
                            ),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return dateTimeShort.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'ofd_at',
                        Header: 'OFD At',
                        accessor: (order) => new Date(order?.ltl_pickup_completed),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return dateTimeShort.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'arrived_at',
                        Header: 'Arrived At',
                        accessor: (order) => new Date(order?.ltl_dropoff_arrival),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return dateTimeShort.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'completed_at',
                        Header: 'Completed At',
                        accessor: (order) => new Date(order?.completion_time),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return dateTimeShort.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'pod_present',
                        Header: 'POD',
                        accessor: (order) => order?.pod,
                        Cell: ({ value }) => {
                            const hasAny =
                                (Array.isArray(value) && value.length > 0) ||
                                (typeof value === 'string' && value.trim().length > 0) ||
                                (!!value && typeof value === 'object' && Object.keys(value).length > 0);
                            return hasAny ? <CheckIcon style={{ color: colors.greens.primary }} /> : '-';
                        },
                        width: 90,
                        disableSortBy: true,
                    },
                    {
                        id: 'photos_present',
                        Header: 'Photos',
                        accessor: (order) => order?.delivery_photo,
                        Cell: ({ value }) => {
                            const hasAny =
                                (Array.isArray(value) && value.length > 0) ||
                                (typeof value === 'string' && value.trim().length > 0) ||
                                (!!value && typeof value === 'object' && Object.keys(value).length > 0);
                            return hasAny ? <CheckIcon style={{ color: colors.greens.primary }} /> : '-';
                        },
                        width: 110,
                        disableSortBy: true,
                    },
                ],
            },
            {
                sticky: 'right',
                Header: '',
                id: 'sticky-right',
                columns: [
                    ...(tab?.value === 'RECEIVED'
                        ? [
                              {
                                  id: 'daysOnDock',
                                  Header: 'Days on Dock',
                                  accessor: (order) => getStorageDays(order),
                                  disableSortBy: true,
                                  Cell: ({ value }) => {
                                      const display = Number.isFinite(value) ? value : '-';
                                      return (
                                          <BodyCellText
                                              css={css`
                                                  ${Number.isFinite(value) &&
                                                  value > 7 &&
                                                  `color: ${colors.reds.primary}`}
                                              `}
                                          >
                                              {display}
                                          </BodyCellText>
                                      );
                                  },
                              },
                          ]
                        : []),
                    {
                        id: 'milestone',
                        Header: 'Status',
                        accessor: (order) => calcMilestone(order) || '-',
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            if (!value || value === '-') {
                                return value;
                            }
                            return <span css={getMilestoneStyles(value)}>{value}</span>;
                        },
                    },
                    {
                        id: 'show_hidden_columns',
                        Header: '',
                        accessor: () => '',
                        width: 52,
                        minWidth: 52,
                        maxWidth: 52,
                        disableSortBy: true,
                        disableResizing: true,
                        Cell: ({ row }) => {
                            const order = row?.original;
                            const editable = tab?.value !== 'INCOMPLETE' && callbacks?.isOrderEditable?.(order);
                            const disabled = !editable;

                            return (
                                <Tooltip
                                    title={disabled ? 'Editing disabled for this order' : 'Edit order'}
                                    placement="left"
                                    arrow
                                >
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            disabled={disabled}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                callbacks?.onEditOrder?.(order);
                                            }}
                                        >
                                            <CreateIcon
                                                fontSize="small"
                                                style={{
                                                    color: disabled ? colors.greys.primary : '#59b863',
                                                }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            );
                        },
                    },
                ],
            },
        ];
    }, [accountType, user_id, tab, onwardAdminsById, roles, callbacks, partnerCarriersById]);
};
